import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import PageMeta from '../seo/PageMeta';
import JsonLd from '../seo/JsonLd';
import NotFound from '../NotFound';
import { getSiteOrigin } from '../../utils/siteOrigin';
import { openAdobeExchange } from '../../utils/analytics';
import { formatBlogDate } from '../../utils/formatBlogDate';
import { computeArticleSeo } from '../../utils/articleSeo';
import { buildTocFromMarkdown } from '../../utils/markdownToc';
import { useActiveTocSection } from '../../hooks/useActiveTocSection';
import {
  fetchBlogManifest,
  fetchPostMarkdown,
  postDatePublished,
  publicBlogAssetPath,
  type ParsedPost,
} from '../../utils/blogMarkdown';
import './BlogPost.css';

const TOC_SCROLL_OFFSET_PX = 96;

function BlogPost() {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const slug = slugParam?.trim() ?? '';
  const [parsed, setParsed] = useState<ParsedPost | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      setParsed(null);
      try {
        const allowed = await fetchBlogManifest();
        if (cancelled) return;
        if (!allowed.includes(slug)) {
          setNotFound(true);
          return;
        }
        const post = await fetchPostMarkdown(slug);
        if (cancelled) return;
        setParsed(post);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (notFound && !loading) {
    return <NotFound />;
  }

  if (loading || !parsed) {
    return (
      <main className="blog-page">
        <div className="blog-loading" role="status">
          <div className="blog-loading-spinner" aria-hidden />
          <p>Loading article…</p>
        </div>
      </main>
    );
  }

  return <BlogPostArticle slug={slug} parsed={parsed} />;
}

type BlogPostArticleProps = {
  slug: string;
  parsed: ParsedPost;
};

function BlogPostArticle({ slug, parsed }: BlogPostArticleProps) {
  const origin = getSiteOrigin().replace(/\/$/, '');
  const seo = useMemo(() => computeArticleSeo(parsed), [parsed]);
  const datePublished = postDatePublished(parsed);

  const articleJsonLd = useMemo(() => {
    const url = `${origin}/blog/${encodeURIComponent(parsed.slug)}`;
    const data: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: seo.articleTitle,
      description: seo.metaDescription,
      datePublished: datePublished ?? undefined,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      url,
      author: {
        '@type': 'Organization',
        name: 'Substyle / CODEC422',
      },
      publisher: {
        '@type': 'Organization',
        name: 'CODEC422',
        url: origin || undefined,
      },
    };
    if (seo.ogImageAbsoluteUrl) {
      data.image = [seo.ogImageAbsoluteUrl];
    }
    return data;
  }, [parsed.slug, seo, datePublished, origin]);

  const proseRef = useRef<HTMLDivElement>(null);
  const toc = useMemo(
    () => buildTocFromMarkdown(parsed.body),
    [parsed.body]
  );
  const tocIds = useMemo(() => toc.map((t) => t.id), [toc]);
  const activeId = useActiveTocSection(tocIds, TOC_SCROLL_OFFSET_PX);

  useLayoutEffect(() => {
    const root = proseRef.current;
    if (!root) return;
    const headingEls = root.querySelectorAll<HTMLHeadingElement>('h2, h3, h4');
    headingEls.forEach((el, i) => {
      if (i < toc.length) {
        el.id = toc[i].id;
      } else {
        el.removeAttribute('id');
      }
    });
  }, [toc, parsed.body]);

  useEffect(() => {
    const raw = window.location.hash.replace(/^#/, '');
    if (!raw || !tocIds.includes(raw)) return;
    const el = document.getElementById(raw);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
  }, [tocIds]);

  const scrollToHeading = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const nextHash = `#${id}`;
    if (window.location.hash !== nextHash) {
      const path = `${window.location.pathname}${window.location.search}`;
      window.history.replaceState(null, '', `${path}${nextHash}`);
    }
  };

  const onTocClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    scrollToHeading(id);
  };

  const hasToc = toc.length > 0;

  const markdownUrlTransform = (uri: string) => {
    if (uri.startsWith('/') && !uri.startsWith('//')) {
      return publicBlogAssetPath(uri);
    }
    return uri;
  };

  return (
    <main className="blog-page">
      <PageMeta
        title={seo.documentTitle}
        description={seo.metaDescription}
        canonicalPath={`/blog/${slug}`}
        ogType="article"
        articlePublishedTime={datePublished}
        keywords={seo.keywords}
        ogImage={seo.ogImageAbsoluteUrl}
      />
      <JsonLd
        id={`substyle-article-jsonld-${slug}`}
        data={articleJsonLd as Record<string, unknown>}
      />

      <article className="blog-article-section">
        <header className="blog-article-hero">
          <div className="container">
            <div className="blog-article-toolbar">
              <Link to="/blog" className="blog-back">
                ← All posts
              </Link>
            </div>
            <div className="blog-article-hero-inner blog-article-header">
              {datePublished && (
                <time dateTime={datePublished}>
                  {formatBlogDate(datePublished)}
                </time>
              )}
              <h1>{seo.articleTitle}</h1>
              <p className="blog-article-dek">{seo.displaySummary}</p>
            </div>
          </div>
        </header>

        <div className="container">
          <div
            className={
              hasToc
                ? 'blog-article-body'
                : 'blog-article-body blog-article-body--no-toc'
            }>
            <div className="blog-article-main-col">
              <div ref={proseRef} className="blog-prose">
                <ReactMarkdown urlTransform={markdownUrlTransform}>
                  {parsed.body}
                </ReactMarkdown>
              </div>
              <aside className="blog-cta-panel" aria-label="Try Substyle">
                <p>
                  Create animated subtitles on your timeline in Premiere Pro
                  with Substyle—word-level timing, presets, and no round-trip
                  exports.
                </p>
                <button
                  type="button"
                  className="cta-button primary"
                  onClick={() => openAdobeExchange('blog_post')}
                >
                  Install
                </button>
              </aside>
            </div>

            {hasToc && (
              <nav className="blog-toc" aria-label="On this page">
                <p className="blog-toc-title">On this page</p>
                <ul className="blog-toc-list">
                  {toc.map((item) => (
                    <li
                      key={item.id}
                      className={`blog-toc-li blog-toc-li--level-${item.level}`}>
                      <a
                        href={`#${item.id}`}
                        className={
                          activeId === item.id
                            ? 'blog-toc-link is-active'
                            : 'blog-toc-link'
                        }
                        data-level={item.level}
                        onClick={(e) => onTocClick(e, item.id)}>
                        {item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </div>
      </article>
    </main>
  );
}

export default BlogPost;
